import { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Flex from '../../../components/base/Flex';
import OutlinedButton from '../../../components/base/OutlinedButton';
import ContainedButton from '../../../components/base/ContainedButton';
import CustomModal from '../../../components/base/CustomModal';
import { toast } from 'react-toastify';
import { useScandiumQuery } from '../../../data-layer/utils';
import { SelectDropdown } from './GithubConnectModal';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import StepContent from '@mui/material/StepContent';

const STEPS = {
  WORKSPACE: 0,
  SPACE: 1,
  FOLDER: 2,
  LIST: 3,
  COMPLETE: 4
};

const STEP_LABELS = [
  'Select Workspace',
  'Select Space',
  'Select Folder',
  'Select List',
  'Complete'
];

const ClickUpConnectModal = ({
  open,
  onClose,
  onComplete,
  updateChannelData,
  successChannels,
  errorChannels,
  activeTab,
  isClickUpTokenSuccess,
  showClickUpSettings,
  handlePostChannels
}) => {
  const [currentStep, setCurrentStep] = useState(STEPS.WORKSPACE);
  const activeChannel = activeTab === 0 ? successChannels : errorChannels;
  const clickupChannel = activeChannel.find((channel) => channel.channel === 'clickup');
  const activeWorkspace = clickupChannel?.channel_details?.data;

  const [workspaces, setWorkspaces] = useState([]);
  const [workspaceSpaces, setWorkspaceSpaces] = useState([]);
  const [spaceFolders, setSpaceFolders] = useState([]);
  const [folderLists, setFolderLists] = useState([]);

  const [selectedWorkspace, setSelectedWorkspace] = useState(null);
  const [selectedWorkspacesSpace, setSelectedWorkspaceSpaces] = useState(null);
  const [selectedSpaceFolder, setSelectedSpaceFolder] = useState(null);
  const [selectedFolderLists, setSelectedFolderLists] = useState(null);

  useEffect(() => {
    const matchingWorkspace = workspaces?.find(
      (workspace) => workspace.id === activeWorkspace?.workspace_id
    );
    const matchingSpace = workspaceSpaces?.find(
      (space) => space.id === activeWorkspace?.workspace_space_id
    );
    const matchingSpaceFolder = spaceFolders?.find(
      (folder) => folder.id === activeWorkspace?.space_folder_id
    );
    const matchingFolderList = folderLists?.find((list) => list.id === activeWorkspace?.list_id);

    setSelectedWorkspace(matchingWorkspace || workspaces?.[0]);
    setSelectedWorkspaceSpaces(matchingSpace || workspaceSpaces?.[0]);
    setSelectedSpaceFolder(matchingSpaceFolder || spaceFolders?.[0]);
    setSelectedFolderLists(matchingFolderList || folderLists?.[0]);
  }, [activeChannel, clickupChannel, workspaces, workspaceSpaces, spaceFolders, folderLists]);

  const handleClose = () => {
    setCurrentStep(STEPS.WORKSPACE);
    onClose();
  };

  const handleWorkspaceChange = (event) => {
    setSelectedWorkspace(event.target.value);
    setCurrentStep(STEPS.SPACE);
  };

  const handleWorkspaceSpacesChange = (event) => {
    setSelectedWorkspaceSpaces(event.target.value);
    setCurrentStep(STEPS.FOLDER);
  };

  const handleSpaceFoldersChange = (event) => {
    setSelectedSpaceFolder(event.target.value);
    setCurrentStep(STEPS.LIST);
  };

  const handleFolderListsChange = (event) => {
    setSelectedFolderLists(event.target.value);
  };

  const handleSaveClickUpData = async () => {
    if (
      !selectedWorkspace ||
      !selectedWorkspacesSpace ||
      !selectedSpaceFolder ||
      !selectedFolderLists
    ) {
      toast.error('All fields are required');
      return;
    }

    updateChannelData(
      'clickup',
      {
        workspace_id: selectedWorkspace?.id,
        workspace_space_id: selectedWorkspacesSpace?.id,
        space_folder_id: selectedSpaceFolder?.id,
        list_id: selectedFolderLists?.id
      },
      handlePostChannels
    );

    setCurrentStep(STEPS.COMPLETE);
    toast.success('ClickUp connected successfully!');

    setTimeout(() => {
      handleClose();
    }, 3000);
  };

  const { isLoading: isFetchingWorkspaces } = useScandiumQuery(
    '/third-parties/clickup/workspaces',
    {
      enabled: !!showClickUpSettings || isClickUpTokenSuccess,
      onSuccess: (data) => {
        const matchingWorkspace = data.data.find(
          (workspace) => workspace.id === activeWorkspace?.workspace_id
        );
        setSelectedWorkspace(matchingWorkspace || data?.data?.[0]);
        setWorkspaces(data.data);
      },
      onError: (error) => {
        toast.error(error.message);
      }
    }
  );

  const { isLoading: isFetchingWorkspaceSpaces } = useScandiumQuery(
    `/third-parties/clickup/workspaces/${selectedWorkspace?.id}/spaces`,
    {
      enabled: !!selectedWorkspace?.id,
      onSuccess: (data) => {
        const matchingWorkspaceSpace = data.data.find(
          (workspace) => workspace.id === activeWorkspace?.workspace_space_id
        );
        setSelectedWorkspaceSpaces(matchingWorkspaceSpace || data?.data?.[0]);
        setWorkspaceSpaces(data.data);
      },
      onError: (error) => {
        toast.error(error.message);
      }
    }
  );

  const { isLoading: isFetchingSpaceFolders } = useScandiumQuery(
    `/third-parties/clickup/spaces/${selectedWorkspacesSpace?.id}/folders`,
    {
      enabled: !!selectedWorkspacesSpace?.id,
      onSuccess: (data) => {
        const matchingSpaceFolder = data.data.find(
          (folder) => folder.id === activeWorkspace?.space_folder_id
        );
        setSelectedSpaceFolder(matchingSpaceFolder || data?.data?.[0]);
        setSpaceFolders(data.data);
      },
      onError: (error) => {
        toast.error(error.message);
      }
    }
  );

  const { isLoading: isFetchingFolderLists } = useScandiumQuery(
    `/third-parties/clickup/folders/${selectedSpaceFolder?.id}/lists`,
    {
      enabled: !!selectedSpaceFolder?.id,
      onSuccess: (data) => {
        const matchingFolderList = data.data.find((list) => list.id === activeWorkspace?.list_id);
        setSelectedFolderLists(matchingFolderList || data?.data?.[0]);
        setFolderLists(data.data);
      },
      onError: (error) => {
        toast.error(error.message);
      }
    }
  );

  const getStepContent = (step) => {
    switch (step) {
      case STEPS.WORKSPACE:
        return (
          <Box>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Select the workspace you want to connect from your ClickUp account.
            </Typography>
            <SelectDropdown
              options={workspaces}
              isLoading={isFetchingWorkspaces}
              onChange={handleWorkspaceChange}
              value={selectedWorkspace}
              label={'Select workspace'}
              labelId={'Workspaces'}
            />
          </Box>
        );

      case STEPS.SPACE:
        return (
          <Box>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Select a space from {selectedWorkspace?.name}.
            </Typography>
            <SelectDropdown
              options={workspaceSpaces}
              isLoading={isFetchingWorkspaceSpaces}
              onChange={handleWorkspaceSpacesChange}
              value={selectedWorkspacesSpace}
              label={'Select space'}
              labelId={'Workspace spaces'}
            />
          </Box>
        );

      case STEPS.FOLDER:
        return (
          <Box>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Select a folder from {selectedWorkspacesSpace?.name}.
            </Typography>
            <SelectDropdown
              options={spaceFolders}
              isLoading={isFetchingSpaceFolders}
              onChange={handleSpaceFoldersChange}
              value={selectedSpaceFolder}
              label={'Select a space folder'}
              labelId={'Space Folders'}
            />
          </Box>
        );

      case STEPS.LIST:
        return (
          <Box>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Select a list from {selectedSpaceFolder?.name}.
            </Typography>
            <SelectDropdown
              options={folderLists}
              isLoading={isFetchingFolderLists}
              onChange={handleFolderListsChange}
              value={selectedFolderLists}
              label={'Select a folder list'}
              labelId={'Folder list'}
            />
          </Box>
        );

      case STEPS.COMPLETE:
        return (
          <Box textAlign="center" py={2}>
            <Typography variant="h6" color="success.main" gutterBottom>
              ✅ Connected Successfully!
            </Typography>
            <Typography variant="body2" color="text.secondary">
              ClickUp is now connected to {selectedWorkspace?.name} - {selectedSpaceFolder?.name} -{' '}
              {selectedFolderLists?.name}
            </Typography>
          </Box>
        );

      default:
        return null;
    }
  };

  const getStepActions = (step) => {
    switch (step) {
      case STEPS.WORKSPACE:
        return (
          <Flex columnGap={2} justifyContent="flex-end">
            <OutlinedButton onClick={handleClose}>Cancel</OutlinedButton>
            <ContainedButton
              onClick={() => selectedWorkspace && setCurrentStep(STEPS.SPACE)}
              disabled={!selectedWorkspace}>
              Next
            </ContainedButton>
          </Flex>
        );

      case STEPS.SPACE:
        return (
          <Flex columnGap={2} justifyContent="space-between">
            <OutlinedButton onClick={() => setCurrentStep(STEPS.WORKSPACE)}>Back</OutlinedButton>
            <Flex columnGap={2}>
              <OutlinedButton onClick={handleClose}>Cancel</OutlinedButton>
              <ContainedButton
                onClick={() => selectedWorkspacesSpace && setCurrentStep(STEPS.FOLDER)}
                disabled={!selectedWorkspacesSpace}>
                Next
              </ContainedButton>
            </Flex>
          </Flex>
        );

      case STEPS.FOLDER:
        return (
          <Flex columnGap={2} justifyContent="space-between">
            <OutlinedButton onClick={() => setCurrentStep(STEPS.SPACE)}>Back</OutlinedButton>
            <Flex columnGap={2}>
              <OutlinedButton onClick={handleClose}>Cancel</OutlinedButton>
              <ContainedButton
                onClick={() => selectedSpaceFolder && setCurrentStep(STEPS.LIST)}
                disabled={!selectedSpaceFolder}>
                Next
              </ContainedButton>
            </Flex>
          </Flex>
        );

      case STEPS.LIST:
        return (
          <Flex columnGap={2} justifyContent="space-between">
            <OutlinedButton onClick={() => setCurrentStep(STEPS.FOLDER)}>Back</OutlinedButton>
            <Flex columnGap={2}>
              <OutlinedButton onClick={handleClose}>Cancel</OutlinedButton>
              <ContainedButton onClick={handleSaveClickUpData} disabled={!selectedFolderLists}>
                Connect
              </ContainedButton>
            </Flex>
          </Flex>
        );

      case STEPS.COMPLETE:
        return (
          <Flex columnGap={2} justifyContent="flex-end">
            <ContainedButton onClick={handleClose}>Done</ContainedButton>
          </Flex>
        );

      default:
        return null;
    }
  };

  return (
    <CustomModal open={open} onClose={handleClose}>
      <Box py={2} px={2} width="80vw" maxWidth="500px">
        <Typography variant="h4" color="primary" gutterBottom>
          Connect Your ClickUp Account
        </Typography>

        <Typography variant="body1" color="text.secondary" mb={3}>
          {`The modal allows you to choose a Clickup workspace. When a test fails or passes, a card
          will be created in the selected folder list to track the outcome.`}
        </Typography>

        <Stepper activeStep={currentStep} orientation="vertical" sx={{ mb: 3 }}>
          {STEP_LABELS.map((label, index) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
              <StepContent>
                {getStepContent(index)}
                <Box sx={{ mt: 2 }}>{getStepActions(index)}</Box>
              </StepContent>
            </Step>
          ))}
        </Stepper>
      </Box>
    </CustomModal>
  );
};

export default ClickUpConnectModal;
